import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useEffect,
  useState
} from 'react'
import { socket } from "../index"
import { useAuth } from './auth'

interface SparePartsContext {
  spareParts: SparePart[],
  currencyIcons: Record<string, string>,
  distributors: string[],
  pagesCount: number,
  search: string,
  sortByItem: "default" | "ascending" | "descending",
  filterByModelAndVersion: string,
  displayMode: string,
  currentPage: number,
  currencies: string,
  companyName: string,
  itemsPerPage: number,
  searchResults: number,
  selectedItem: SparePart | null,
  setSearch: Dispatch<SetStateAction<string>>,
  setSortByItem: Dispatch<SetStateAction<"default" | "ascending" | "descending">>,
  setFilterByModelAndVer: Dispatch<SetStateAction<string>>,
  setDisplayMode: Dispatch<SetStateAction<string>>,
  setCurrentPage: Dispatch<SetStateAction<number>>,
  setCurrencies: Dispatch<SetStateAction<string>>,
  setCompanyName: Dispatch<SetStateAction<string>>,
  setItemsPerPage: Dispatch<SetStateAction<number>>,
  setSearchResults: Dispatch<SetStateAction<number>>,
  setSelectedItem: Dispatch<SetStateAction<SparePart | null>>
}

export interface SparePart {
  Item: string
  Description: string
  ModelAndVer: string
  Currency: string
  Price: number
  DiscountedPrice: number
  Picture: string
  Specification: string
  State: string
}

const SpareParts = createContext({} as SparePartsContext)

export default function SparePartsProvider(
  { children }: { children: ReactNode }
) {
  const {user, currentRole} = useAuth()
  const [spareParts, setSpareParts] = useState<SparePart[]>([]);
  const [search, setSearch] = useState<string>('')
  const [sortByItem, setSortByItem] = useState<"default" | "ascending" | "descending">("default");
  const [filterByModelAndVersion, setFilterByModelAndVer] = useState<string>("")

  const [displayMode, setDisplayMode] = useState<string>(() => {
    return "list";
    // Commented out for the time being (grid view was not desired, we're using list view instead)
    // const savedDisplayMode = localStorage.getItem("sparePartsDIsplayedMode");
    // return JSON.parse(savedDisplayMode!) || "list";
  })
  const currencyIcons: Record<string, string> = {
    EUR: "€",
    USD: "$",
    JPY: "¥",
    GBP: "£",
    SEK: "SEK"
  };
  const [currencies, setCurrencies] = useState<string>("EUR");
  const [companyName, setCompanyName] = useState<string>("");
  const [pagesCount, setPagesCount] = useState<number>(0)
  const [searchResults, setSearchResults] = useState<number>(0)
  const [currentPage, setCurrentPage] = useState<number>(1)
  const [itemsPerPage, setItemsPerPage] = useState<number>(15)
  const [selectedItem, setSelectedItem] = useState<SparePart | null>(null)
  const [distributors, setDistributors] = useState<string[]>([]);

  useEffect(() => {
    socket.emit('get_distributors');
    socket.on('distributors_received', (data) => {
      if (data.error) {
        console.error(data.error)
      } else {
        setDistributors(data.distributors.sort())
      }
    })
    return () => {
      socket.off('distributors_received')
    }
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    // We shall not select the company names for Admins (and the company selector should be empty)
    if (currentRole.includes("Administrator")) {
      setCompanyName("");
    } else if (user?.companies) {
      setCompanyName(user.companies)
    }
  }, [user, currentRole])

  const updateSpareParts = (
    companyName: string,
    currencies: string,
    search: string,
    currentPage: number,
    itemsPerPage: number,
    sortByItem: string,
    filterByModelAndVersion: string
  ) => {
    socket.emit('get_spare_parts', companyName.split(";")[0], currencies, search, currentPage, itemsPerPage, sortByItem.toLowerCase(), filterByModelAndVersion)
  }

  const getPrefCurrency = (
    companyName: string
  ) => {
    socket.emit('get_preffered_currency', companyName.split(";")[0])
  }

  useEffect(() => {
    getPrefCurrency(companyName)
    // eslint-disable-next-line
  }, [companyName])

  useEffect(() => {
    const prefCurrencyReceived = function (data: any) {
      setCurrencies(data.preferredCurrency);
    }
    socket.on('preffered_currency_received', prefCurrencyReceived);
    return () => {
      socket.off('preffered_currency_received', prefCurrencyReceived);
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const sparePartsReceived = function (data: any) {
      if (data.error) {
        console.error(data.error);
      } else {
        if (selectedItem) {
          let updateSelectedItem = data.allUsers.find((sparePartsIterator: SparePart) => sparePartsIterator.Item === selectedItem?.Item)
          setSelectedItem(updateSelectedItem)
        }

        if (data.sparePartsPagesCount < currentPage) {
          setCurrentPage(1)
        }

        setSpareParts(data.spareParts || [])
        setPagesCount(data.sparePartsPagesCount)
        setSearchResults(data.searchResults)
      }
    }
    socket.on('spare_parts_received', sparePartsReceived)
    return () => {
      socket.off('spare_parts_received');
    }
    // eslint-disable-next-line
  }, [search, currentPage, searchResults])

  useEffect(() => {
    updateSpareParts(companyName, currencies, search, currentPage, itemsPerPage, sortByItem, filterByModelAndVersion)
    // eslint-disable-next-line
  }, [companyName, currencies, search, currentPage, itemsPerPage, sortByItem, filterByModelAndVersion, currentRole, searchResults])

  useEffect(() => {
    socket.on('spare_parts_modified', updateSpareParts)
    return () => {
      socket.off('spare_parts_modified');
    }
    // eslint-disable-next-line
  }, []);

  // Commented out for the time being (grid view was not desired, we're using list view instead)
  // useEffect(() => {
  //   localStorage.setItem("sparePartsDIsplayedMode", JSON.stringify(displayMode))
  // }, [displayMode])

  return (
    <SpareParts.Provider
      value={{
        spareParts,
        currencyIcons,
        distributors,
        pagesCount,
        search,
        sortByItem,
        filterByModelAndVersion,
        displayMode,
        currentPage,
        currencies,
        companyName,
        itemsPerPage,
        searchResults,
        selectedItem,
        setSearch,
        setSortByItem,
        setFilterByModelAndVer,
        setDisplayMode,
        setCurrentPage,
        setCurrencies,
        setCompanyName,
        setItemsPerPage,
        setSearchResults,
        setSelectedItem,
      }}
    >
      {children}
    </SpareParts.Provider>
  )
}

export const useSpareParts = () => useContext(SpareParts)